import {
  Box,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  SimpleGrid,
  Textarea,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UuidPreview from "./UuidPreview";
import CodesetSelect from "./CodesetSelect";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";

const PermissionDefinitionsEditor = ({ permissionDefinition, onClose, isNew }) => {
  const [permissionDefinitionData, setPermissionDefinitionData] = useState({});
  const [permissionRules, setPermissionRules] = useState([]);
  const [permissionRulesLoading, setPermissionRulesLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (permissionDefinition) {
      setPermissionDefinitionData({ ...permissionDefinition });
    }
  }, [permissionDefinition]);

  useEffect(() => {
    const fetchPermissionRules = async () => {
      if (!isNew && permissionDefinition?.code) {
        try {
          const res = await fetch("https://permissions.poc.mrc.ai/api/rules");
          const data = await res.json();
          const filtered = data.filter((rule) => rule.permissionCode === permissionDefinition.code);
          setPermissionRules(filtered);
        } catch (error) {
          console.error("Error fetching permission rules:", error);
        } finally {
          setPermissionRulesLoading(false);
        }
      } else {
        setPermissionRulesLoading(false);
      }
    };

    fetchPermissionRules();
  }, [isNew, permissionDefinition]);

  const handleFieldChange = (field, value) => {
    setPermissionDefinitionData((prev) => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  };

  const handleSave = async () => {
    try {
      // Check if the code has changed and we have existing rules
      const codeChanged = !isNew && permissionDefinition?.code !== permissionDefinitionData.code;
      const oldCode = permissionDefinition?.code;

      const response = await fetch(
        isNew
          ? "https://permissions.poc.mrc.ai/api/definitions"
          : `https://permissions.poc.mrc.ai/api/definitions/${permissionDefinitionData._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(permissionDefinitionData),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "save"} permission definition. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      // If the code changed, update all associated rules
      if (codeChanged && permissionRules.length > 0) {
        const updatePromises = permissionRules.map(async (rule) => {
          const updatedRule = { ...rule, permissionCode: permissionDefinitionData.code };
          const ruleResponse = await fetch(
            `https://permissions.poc.mrc.ai/api/rules/${rule._id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(updatedRule),
            }
          );
          if (!ruleResponse.ok) {
            throw new Error(`Failed to update rule ${rule._id}`);
          }
          return updatedRule;
        });

        const updatedRules = await Promise.all(updatePromises);
        setPermissionRules(updatedRules);
      }

      toast({
        title: isNew ? "Permission Definition created" : "Permission Definition saved",
        description: isNew
          ? "New permission definition was added."
          : codeChanged
          ? "Changes saved successfully. Associated rules updated."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/permissiondefinitions/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving permission definition:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the permission definition.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleAddRule = async () => {
    if (!permissionDefinitionData.code) {
      toast({
        title: "Cannot add rule",
        description: "Please save the permission definition first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newRule = {
      _id: crypto.randomUUID(),
      permissionCode: permissionDefinitionData.code,
      requiredRoles: [],
      requiredParticipations: [],
      createdDate: new Date().toISOString(),
    };

    try {
      const response = await fetch("https://permissions.poc.mrc.ai/api/rules", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newRule),
      });

      if (!response.ok) {
        throw new Error("Failed to create rule");
      }

      const result = await response.json();
      setPermissionRules([...permissionRules, result]);

      toast({
        title: "Rule created",
        description: "New permission rule was added.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("❌ Error creating rule:", error);
      toast({
        title: "Failed to create rule",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleUpdateRule = async (ruleId, field, value) => {
    const updatedRules = permissionRules.map((rule) =>
      rule._id === ruleId ? { ...rule, [field]: value } : rule
    );
    setPermissionRules(updatedRules);

    const ruleToUpdate = updatedRules.find((rule) => rule._id === ruleId);

    try {
      const response = await fetch(`https://permissions.poc.mrc.ai/api/rules/${ruleId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ruleToUpdate),
      });

      if (!response.ok) {
        throw new Error("Failed to update rule");
      }

      toast({
        title: "Rule updated",
        description: "Permission rule was updated successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("❌ Error updating rule:", error);
      toast({
        title: "Failed to update rule",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      const response = await fetch(`https://permissions.poc.mrc.ai/api/rules/${ruleId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete rule");
      }

      setPermissionRules(permissionRules.filter((rule) => rule._id !== ruleId));

      toast({
        title: "Rule deleted",
        description: "Permission rule was removed.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("❌ Error deleting rule:", error);
      toast({
        title: "Failed to delete rule",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate("/Poc");
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Permission</Heading>
                <Tooltip label="Save Permission" aria-label="Save Permission Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Definition</Tab>
              <Tab>Rules ({permissionRules.length})</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <UuidPreview uuid={permissionDefinitionData._id} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={4}>
                    <FormControl>
                      <FormLabel>Code</FormLabel>
                      <Input
                        value={permissionDefinitionData.code || ""}
                        onChange={(e) =>
                          handleFieldChange("code", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Description</FormLabel>
                      <Textarea
                        value={permissionDefinitionData.description || ""}
                        onChange={(e) =>
                          handleFieldChange("description", e.target.value)
                        }
                        rows={3}
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <HStack alignItems="center" mb={4}>
                    <Heading size="md">Rules for {permissionDefinitionData.code}</Heading>
                    <IconButton
                      icon={<HiOutlinePlus />}
                      size="md"
                      onClick={handleAddRule}
                      aria-label="Add Rule"
                      isDisabled={isNew}
                    />
                  </HStack>
                  {isNew ? (
                    <Box p={4} textAlign="center" color="gray.500">
                      Save the permission definition first to add rules.
                    </Box>
                  ) : (
                    <VStack spacing={4} align="stretch">
                      {permissionRules.map((rule) => (
                        <Box
                          key={rule._id}
                          p={4}
                          borderWidth="1px"
                          borderRadius="md"
                          borderColor="gray.300"
                          bg="white">
                          <HStack justify="space-between" mb={3}>
                            <Heading size="sm">Rule {rule._id.substring(0, 8)}...</Heading>
                            <IconButton
                              icon={<HiOutlineTrash />}
                              size="sm"
                              onClick={() => handleDeleteRule(rule._id)}
                              aria-label="Delete Rule"
                            />
                          </HStack>

                          <VStack spacing={3} align="stretch">
                            {/* Required Roles Section */}
                            <Box>
                              <HStack mb={2}>
                                <Heading size="xs">Required Roles</Heading>
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  size="xs"
                                  onClick={() => {
                                    const updatedRoles = [...(rule.requiredRoles || []), ""];
                                    handleUpdateRule(rule._id, "requiredRoles", updatedRoles);
                                  }}
                                  aria-label="Add Role"
                                />
                              </HStack>
                              {rule.requiredRoles && rule.requiredRoles.length > 0 ? (
                                <VStack spacing={2} align="stretch">
                                  {rule.requiredRoles.map((roleCode, roleIndex) => (
                                    <HStack key={roleIndex} spacing={2}>
                                      <CodesetSelect
                                        codesetCode="MRCAI004"
                                        value={roleCode ? { code: roleCode, description: "" } : null}
                                        onChange={(val) => {
                                          const updatedRoles = [...rule.requiredRoles];
                                          updatedRoles[roleIndex] = val.code;
                                          handleUpdateRule(rule._id, "requiredRoles", updatedRoles);
                                        }}
                                        size="sm"
                                      />
                                      <IconButton
                                        icon={<HiOutlineTrash />}
                                        size="sm"
                                        onClick={() => {
                                          const updatedRoles = rule.requiredRoles.filter((_, i) => i !== roleIndex);
                                          handleUpdateRule(rule._id, "requiredRoles", updatedRoles);
                                        }}
                                        aria-label="Remove Role"
                                      />
                                    </HStack>
                                  ))}
                                </VStack>
                              ) : (
                                <Box fontSize="sm" color="gray.500">No required roles</Box>
                              )}
                            </Box>

                            {/* Required Participations Section */}
                            <Box>
                              <HStack mb={2}>
                                <Heading size="xs">Required Participations</Heading>
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  size="xs"
                                  onClick={() => {
                                    const updatedParticipations = [...(rule.requiredParticipations || []), ""];
                                    handleUpdateRule(rule._id, "requiredParticipations", updatedParticipations);
                                  }}
                                  aria-label="Add Participation"
                                />
                              </HStack>
                              {rule.requiredParticipations && rule.requiredParticipations.length > 0 ? (
                                <VStack spacing={2} align="stretch">
                                  {rule.requiredParticipations.map((participationCode, participationIndex) => (
                                    <HStack key={participationIndex} spacing={2}>
                                      <CodesetSelect
                                        codesetCode="3035"
                                        value={participationCode ? { code: participationCode, description: "" } : null}
                                        onChange={(val) => {
                                          const updatedParticipations = [...rule.requiredParticipations];
                                          updatedParticipations[participationIndex] = val.code;
                                          handleUpdateRule(rule._id, "requiredParticipations", updatedParticipations);
                                        }}
                                        size="sm"
                                      />
                                      <IconButton
                                        icon={<HiOutlineTrash />}
                                        size="sm"
                                        onClick={() => {
                                          const updatedParticipations = rule.requiredParticipations.filter((_, i) => i !== participationIndex);
                                          handleUpdateRule(rule._id, "requiredParticipations", updatedParticipations);
                                        }}
                                        aria-label="Remove Participation"
                                      />
                                    </HStack>
                                  ))}
                                </VStack>
                              ) : (
                                <Box fontSize="sm" color="gray.500">No required participations</Box>
                              )}
                            </Box>
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Box>
  );
};

export default PermissionDefinitionsEditor;
